/**
 * Etapa 3 — Adequações necessárias. Cada adequação é gravada na hora (depende do id da máquina).
 * O vínculo com um perigo só é oferecido quando a máquina já tem perigos avaliados.
 */
import { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react';
import { Alert, Box, Button, Chip, Grid, MenuItem, Paper, TextField, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { DataTable } from '../../../../../components';
import {
  machineService,
  MachineAdequacy,
  MachineAdequacyPriority,
  MachineAdequacyProgress,
  MachineHazardAssessment,
} from '../../../../../services/machineService';
import { ConfirmDialog } from '../ConfirmDialog';
import { ListStepProps, PRIORITY_LABELS, PROGRESS_LABELS, StepHandle } from '../MachineWizard.types';
import {
  AdequacyDraft,
  AdequacyErrors,
  adequacyToPayload,
  EMPTY_ADEQUACY_DRAFT,
  errorMessage,
  formatDate,
  validateAdequacy,
} from '../machineWizardValidation';

const fieldSx = { '& .MuiFormHelperText-root': { fontSize: '1rem' } };

const draftFrom = (item: MachineAdequacy): AdequacyDraft => ({
  description: item.description,
  priority: item.priority,
  dueDate: item.dueDate ? item.dueDate.slice(0, 10) : '',
  responsible: item.responsible ?? '',
  progress: item.progress,
  hazardAssessmentId: item.hazardAssessmentId === null ? '' : String(item.hazardAssessmentId),
});

export const AdequaciesStep = forwardRef<StepHandle, ListStepProps>(
  ({ machineId, canEdit, onDirtyChange, onSaved }, ref) => {
    const [items, setItems] = useState<MachineAdequacy[]>([]);
    const [hazards, setHazards] = useState<MachineHazardAssessment[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [draft, setDraft] = useState<AdequacyDraft | null>(null);
    const [baseline, setBaseline] = useState<AdequacyDraft>(EMPTY_ADEQUACY_DRAFT);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [errors, setErrors] = useState<AdequacyErrors>({});
    const [submitting, setSubmitting] = useState(false);
    const [removing, setRemoving] = useState<MachineAdequacy | null>(null);

    const dirty = draft !== null && JSON.stringify(draft) !== JSON.stringify(baseline);

    useEffect(() => {
      onDirtyChange(dirty);
    }, [dirty, onDirtyChange]);

    const load = useCallback(async () => {
      try {
        const [adequacies, assessed] = await Promise.all([
          machineService.getAdequacies(machineId),
          machineService.getHazardAssessments(machineId),
        ]);
        setItems(adequacies);
        setHazards(assessed);
        setError(null);
      } catch (err: unknown) {
        setError(errorMessage(err, 'Não foi possível carregar as adequações.'));
      } finally {
        setLoading(false);
      }
    }, [machineId]);

    useEffect(() => {
      load();
    }, [load]);

    const openDraft = (item?: MachineAdequacy) => {
      const initial = item ? draftFrom(item) : EMPTY_ADEQUACY_DRAFT;
      setBaseline(initial);
      setDraft(initial);
      setEditingId(item ? item.id : null);
      setErrors({});
    };

    const closeDraft = () => {
      setDraft(null);
      setEditingId(null);
      setErrors({});
    };

    const save = useCallback(async (): Promise<boolean> => {
      if (!draft) return true;
      const found = validateAdequacy(draft);
      setErrors(found);
      if (Object.keys(found).length > 0) return false;

      setSubmitting(true);
      try {
        const payload = adequacyToPayload(draft);
        if (editingId !== null) await machineService.updateAdequacy(machineId, editingId, payload);
        else await machineService.createAdequacy(machineId, payload);
        await load();
        closeDraft();
        onSaved('Adequação guardada.');
        return true;
      } catch (err: unknown) {
        setError(errorMessage(err, 'Não foi possível guardar a adequação.'));
        return false;
      } finally {
        setSubmitting(false);
      }
    }, [draft, editingId, machineId, load, onSaved]);

    useImperativeHandle(
      ref,
      () => ({
        salvarPendente: async () => {
          if (!draft) return true;
          if (!dirty) {
            closeDraft();
            return true;
          }
          return save();
        },
      }),
      [draft, dirty, save],
    );

    const confirmRemoval = async () => {
      if (!removing) return;
      try {
        await machineService.deleteAdequacy(machineId, removing.id);
        setRemoving(null);
        await load();
        onSaved('Adequação removida.');
      } catch (err: unknown) {
        setRemoving(null);
        setError(errorMessage(err, 'Não foi possível remover a adequação.'));
      }
    };

    const hazardName = (id: number | null): string =>
      hazards.find((hazard) => Number(hazard.id) === Number(id))?.hazard ?? '—';

    const columns = [
      { id: 'description', label: 'Adequação', primary: true, render: (row: MachineAdequacy) => row.description },
      { id: 'hazard', label: 'Perigo que reduz', render: (row: MachineAdequacy) => hazardName(row.hazardAssessmentId) },
      {
        id: 'priority',
        label: 'Prioridade',
        render: (row: MachineAdequacy) => (
          <Chip
            variant="outlined"
            color={row.priority === 'ALTA' ? 'error' : row.priority === 'MEDIA' ? 'warning' : 'default'}
            label={PRIORITY_LABELS[row.priority]}
          />
        ),
      },
      { id: 'dueDate', label: 'Prazo', render: (row: MachineAdequacy) => formatDate(row.dueDate) },
      { id: 'responsible', label: 'Responsável', hideOnMobile: true, render: (row: MachineAdequacy) => row.responsible ?? '—' },
      {
        id: 'progress',
        label: 'Andamento',
        render: (row: MachineAdequacy) => <Chip variant="outlined" label={PROGRESS_LABELS[row.progress]} />,
      },
    ];

    return (
      <Box>
        <Typography variant="h6" component="h3" sx={{ mb: 1 }}>
          O que precisa ser adequado
        </Typography>
        <Typography variant="body1" sx={{ mb: 3 }}>
          Cada item é uma ação para a empresa. Ela aparece no plano de adequações do laudo.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <DataTable<MachineAdequacy>
          embedded
          loading={loading}
          rows={items}
          getRowId={(row) => row.id}
          emptyState={
            canEdit
              ? {
                  title: 'Nenhuma adequação registrada',
                  description: 'Se faltou alguma proteção ou algo está em desacordo, use "Adicionar adequação" para registrar o que precisa ser feito.',
                }
              : {
                  title: 'Nenhuma adequação registrada',
                  description: 'Esta máquina ainda não tem adequações registradas.',
                }
          }
          columns={columns}
          actions={
            canEdit
              ? [
                  { icon: <EditIcon />, tooltip: 'Editar adequação', onClick: (row) => openDraft(row) },
                  { icon: <DeleteIcon />, tooltip: 'Remover adequação', color: 'error', onClick: (row) => setRemoving(row) },
                ]
              : undefined
          }
        />

        {canEdit && draft === null && (
          <Button variant="outlined" onClick={() => openDraft()} sx={{ mt: 2, minHeight: 44 }}>
            Adicionar adequação
          </Button>
        )}

        {!canEdit && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Você pode ver as adequações, mas não alterá-las. Peça a quem administra o seu acesso.
          </Alert>
        )}

        {canEdit && draft !== null && (
          <Paper variant="outlined" sx={{ p: 3, mt: 2 }}>
            <Typography variant="h6" component="h4" sx={{ mb: 2 }}>
              {editingId !== null ? 'Editar adequação' : 'Nova adequação'}
            </Typography>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  required
                  label="O que precisa ser feito"
                  value={draft.description}
                  onChange={(event) => setDraft({ ...draft, description: event.target.value })}
                  error={Boolean(errors.description)}
                  helperText={errors.description || 'Ex.: Reposicionar a cortina de luz'}
                  disabled={submitting}
                  sx={fieldSx}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  select
                  fullWidth
                  label="Prioridade"
                  value={draft.priority}
                  onChange={(event) => setDraft({ ...draft, priority: event.target.value as MachineAdequacyPriority })}
                  disabled={submitting}
                  sx={fieldSx}
                >
                  {Object.entries(PRIORITY_LABELS).map(([value, label]) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  type="date"
                  label="Prazo"
                  value={draft.dueDate}
                  onChange={(event) => setDraft({ ...draft, dueDate: event.target.value })}
                  disabled={submitting}
                  helperText="Opcional"
                  slotProps={{ inputLabel: { shrink: true } }}
                  sx={fieldSx}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  select
                  fullWidth
                  label="Andamento"
                  value={draft.progress}
                  onChange={(event) => setDraft({ ...draft, progress: event.target.value as MachineAdequacyProgress })}
                  disabled={submitting}
                  sx={fieldSx}
                >
                  {Object.entries(PROGRESS_LABELS).map(([value, label]) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, md: hazards.length > 0 ? 6 : 12 }}>
                <TextField
                  fullWidth
                  label="Responsável"
                  value={draft.responsible}
                  onChange={(event) => setDraft({ ...draft, responsible: event.target.value })}
                  error={Boolean(errors.responsible)}
                  helperText={errors.responsible || 'Opcional. Quem vai cuidar disso?'}
                  disabled={submitting}
                  sx={fieldSx}
                />
              </Grid>
              {hazards.length > 0 && (
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    select
                    fullWidth
                    label="Perigo que reduz"
                    value={draft.hazardAssessmentId}
                    onChange={(event) => setDraft({ ...draft, hazardAssessmentId: event.target.value })}
                    disabled={submitting}
                    helperText="Opcional. Escolha entre os perigos já avaliados desta máquina."
                    sx={fieldSx}
                  >
                    <MenuItem value="">Nenhum</MenuItem>
                    {hazards.map((hazard) => (
                      <MenuItem key={hazard.id} value={String(hazard.id)}>
                        {hazard.hazard}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              )}
            </Grid>
            <Box sx={{ display: 'flex', gap: 1, mt: 3 }}>
              <Button variant="outlined" onClick={save} disabled={submitting} sx={{ minHeight: 44 }}>
                Guardar adequação
              </Button>
              <Button variant="text" onClick={closeDraft} disabled={submitting} sx={{ minHeight: 44 }}>
                Descartar
              </Button>
            </Box>
          </Paper>
        )}

        <ConfirmDialog
          open={removing !== null}
          title="Remover esta adequação?"
          message="Ela some do plano de adequações da máquina e do laudo."
          confirmLabel="Remover"
          cancelLabel="Manter"
          onConfirm={confirmRemoval}
          onCancel={() => setRemoving(null)}
        />
      </Box>
    );
  },
);

AdequaciesStep.displayName = 'AdequaciesStep';

export default AdequaciesStep;
